import { Link } from 'react-router-dom';
import { Video, Eye, Zap, Code, Users, Calendar, Play, Star } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAnalytics } from '@/hooks/useAnalytics';
import SEOHead from '@/components/SEOHead';

export default function VideoMemoriesPage() {
  const { trackEvent } = useAnalytics();

  const handleFeatureClick = (feature: string) => {
    trackEvent('roadmap_feature_detail_view', {
      feature: 'video_memories',
      detail_section: feature,
      source: 'roadmap_landing_page'
    });
  };

  return (
    <>
      <SEOHead 
        title="Video coming soon"
        description="Video coming soon, get early access. Text and voice are available today."
        keywords={["enterprise video AI", "video memory storage", "video transcription API", "scene analysis", "visual AI development", "multimodal AI memory", "video embeddings", "enterprise AI tools", "meeting video analysis", "video intelligence"]}
        canonicalUrl="https://memara.io/roadmap/video-memories"
        ogType="article"
      />

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-gray-50">
        {/* Hero Section */}
        <section className="relative px-6 py-24 mx-auto max-w-7xl lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-200">
                <Calendar className="w-4 h-4 mr-2" />
                Coming soon
              </Badge>
              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200">
                <Star className="w-4 h-4 mr-2" />
                Enterprise Feature
              </Badge>
            </div>
            
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
              Enterprise Video AI
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-slate-600 to-gray-600">
                Memory & Intelligence
              </span>
            </h1>
            
            <p className="mt-6 text-xl leading-8 text-gray-600 max-w-3xl mx-auto">
              <Link
                to="/roadmap/image-memories#image-interest"
                className="font-medium text-orange-700 underline underline-offset-2"
              >
                Video coming soon, get early access
              </Link>
            </p>
          </div>
        </section>

        {/* Technical Capabilities */}
        <section className="px-6 py-16 mx-auto max-w-7xl lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Enterprise-Grade Video AI Capabilities
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Built for enterprise teams requiring advanced video intelligence and analysis
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-slate-500" onClick={() => handleFeatureClick('video_analysis')}>
              <CardHeader>
                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                  <Eye className="w-6 h-6 text-slate-600" />
                </div>
                <CardTitle>Advanced Scene Analysis</CardTitle>
                <CardDescription>
                  AI-powered video understanding with object and activity recognition
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Object detection and tracking across frames</li>
                  <li>• Activity and gesture recognition</li>
                  <li>• Scene change detection and segmentation</li>
                  <li>• Visual similarity matching</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-blue-500" onClick={() => handleFeatureClick('video_transcription')}>
              <CardHeader>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Video className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle>Video Transcription & Sync</CardTitle>
                <CardDescription>
                  Synchronized speech recognition with visual context
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• High-accuracy speech-to-text with timestamps</li>
                  <li>• Speaker identification with visual cues</li>
                  <li>• Multi-language transcription support</li>
                  <li>• Visual-audio content correlation</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-purple-500" onClick={() => handleFeatureClick('enterprise_intelligence')}>
              <CardHeader>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle>Enterprise Intelligence</CardTitle>
                <CardDescription>
                  Advanced AI analysis for business insights and automation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Meeting insights and action item extraction</li>
                  <li>• Presentation analysis and key point identification</li>
                  <li>• Compliance and security monitoring</li>
                  <li>• Custom model training for specific use cases</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Enterprise Integration Examples */}
        <section className="px-6 py-16 mx-auto max-w-7xl lg:px-8 bg-gray-50">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Built for Enterprise Development Teams
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Enterprise-grade APIs with advanced security and compliance features
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <Card className="border-l-4 border-l-slate-500">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <Code className="w-6 h-6 text-slate-600" />
                  <CardTitle>Store Enterprise Video Memories</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-900 rounded-lg p-4 text-sm">
                  <pre className="text-green-400">
{`// Store video with enterprise metadata
await memara.createMemory({
  content: {
    text: "Q4 board meeting presentation",
    video: [board_meeting_recording],
    metadata: {
      classification: "confidential",
      retention_policy: "7_years",
      classification_tags: ["confidential", "board"]
    }
  },
  space: "enterprise_meetings",
  access_control: {
    viewers: ["board_members", "executives"],
    editors: ["secretary", "legal"]
  }
});`}
                  </pre>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-blue-500">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <Eye className="w-6 h-6 text-blue-600" />
                  <CardTitle>Advanced Video Search & Analysis</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-900 rounded-lg p-4 text-sm">
                  <pre className="text-green-400">
{`// Search video content with AI analysis
const results = await memara.searchMemories({
  query: "presentations about revenue growth",
  contentTypes: ["video"],
  analysis: {
    extract_charts: true,
    identify_speakers: true,
    generate_summary: true,
    action_items: true
  },
  compliance: {
    audit_trail: true,
    access_logging: true
  }
});`}
                  </pre>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Enterprise Use Cases */}
        <section className="px-6 py-16 mx-auto max-w-7xl lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Enterprise Video Intelligence Use Cases
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Real-world applications for enterprise development and business intelligence
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 text-slate-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Board Meeting Intelligence</h3>
                  <p className="text-gray-600">
                    Analyze executive presentations, extract key decisions, and generate 
                    compliance reports. AI-powered insights for governance and strategy.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Code className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Training & Onboarding</h3>
                  <p className="text-gray-600">
                    Store training videos with searchable content. AI-powered skill 
                    assessment and personalized learning path recommendations.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Zap className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Compliance Monitoring</h3>
                  <p className="text-gray-600">
                    Automated compliance checking for recorded meetings and presentations. 
                    Flag sensitive content and ensure regulatory adherence.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Video className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Product Demo Analysis</h3>
                  <p className="text-gray-600">
                    Analyze customer demo videos for engagement patterns, feature interest, 
                    and feedback. Optimize sales presentations with AI insights.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Eye className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Security & Surveillance</h3>
                  <p className="text-gray-600">
                    Intelligent video analysis for security applications. Object tracking, 
                    anomaly detection, and automated incident reporting.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Play className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Knowledge Management</h3>
                  <p className="text-gray-600">
                    Transform video content into searchable knowledge bases. 
                    Extract key information and create automated documentation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Enterprise Features */}
        <section className="px-6 py-16 mx-auto max-w-7xl lg:px-8 bg-gray-50">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Enterprise-Grade Security & Compliance
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Built for enterprise requirements with advanced security and governance
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <Card className="text-center">
              <CardHeader>
                <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🔒</span>
                </div>
                <CardTitle>Advanced Security</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Encrypted in transit and at rest</li>
                  <li>• Role-based access control</li>
                  <li>• Audit trail logging</li>
                  <li>• Tenant isolation</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🏢</span>
                </div>
                <CardTitle>Enterprise Deployment</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• On-premise deployment</li>
                  <li>• Private cloud options</li>
                  <li>• Custom SLA agreements</li>
                  <li>• Dedicated support team</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚖️</span>
                </div>
                <CardTitle>Compliance Ready</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Encryption in transit and at rest</li>
                  <li>• Hosted on AWS</li>
                  <li>• Data residency controls</li>
                  <li>• Retention policy management</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-6 py-24 mx-auto max-w-7xl lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-6">
              Video coming soon
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              <Link
                to="/roadmap/image-memories#image-interest"
                className="font-medium text-orange-700 underline underline-offset-2"
              >
                Video coming soon, get early access
              </Link>
            </p>
          </div>
        </section>
      </div>
    </>
  );
} 